import React, { type ErrorInfo, useCallback } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	AVOID_DEACTIVATION_BANNER_SUBJECT_ID,
	AVOID_DEACTIVATION_BANNER_TEST_ID,
} from '../../utils/constants';
import { getProductNameFromProductKey } from '../../utils/product-terms';
import { AutoUpgradeAvoidDeactivationModal } from '../avoid-deactivation-data-modal';

import type { AutoUpgradeAvoidDeactivationBannerProps } from './types';

const WrapperStyle = xcss({
	padding: 'space.150',
	alignItems: 'center',
	justifyContent: 'space-between',
	overflow: 'hidden',
	background: token('color.background.warning.bold'),
	height: '44px', //height required for jira SSR
});

const InlineStyles = xcss({
	alignItems: 'center',
});

const linkStyles = cssMap({
	modalTakeAction: {
		textDecorationLine: 'underline',
	},
});

export function AutoUpgradeAvoidDeactivationBanner({
	baseProduct,
	autoUpgradedJiraSubProducts,
	cloudId,
}: AutoUpgradeAvoidDeactivationBannerProps): React.JSX.Element {
	const { growthErrorHandler } = useAnalytics();
	const { formatList } = useIntl();

	const errorCallback = useCallback(
		({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo }) => {
			growthErrorHandler({
				error,
				errorInfo,
				product: baseProduct,
				source: AVOID_DEACTIVATION_BANNER_SUBJECT_ID,
			});
		},
		[baseProduct, growthErrorHandler],
	);

	const takeActionFormattedMessageValue = (
		<AutoUpgradeAvoidDeactivationModal
			product={
				autoUpgradedJiraSubProducts !== undefined ? autoUpgradedJiraSubProducts[0] : baseProduct
			}
			touchpointId="avoid-deactivation-banner"
			cloudId={cloudId}
			onError={errorCallback}
		>
			<Text
				weight="bold"
				xcss={linkStyles.modalTakeAction}
				testId={`${AVOID_DEACTIVATION_BANNER_TEST_ID}-take-action-button`}
			>
				<FormattedMessage
					id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-banner.take-action-link"
					defaultMessage="Take action"
					description="Link text in the auto-upgrade avoid deactivation banner, prompting the user to take action to avoid deactivation of their product"
				/>
			</Text>
		</AutoUpgradeAvoidDeactivationModal>
	);

	return (
		<AnalyticsContext
			data={{
				attributes: {
					baseProduct,
					autoUpgradedJiraSubProducts,
					touchpointId: AVOID_DEACTIVATION_BANNER_SUBJECT_ID,
				},
			}}
		>
			<Flex testId={AVOID_DEACTIVATION_BANNER_TEST_ID} xcss={WrapperStyle}>
				<Inline space="space.200" xcss={InlineStyles}>
					<StatusWarningIcon
						color={token('color.text')}
						label="deactivation date warning icon"
						testId={`${AVOID_DEACTIVATION_BANNER_TEST_ID}-warning-icon`}
					/>

					<Box>
						{baseProduct === 'confluence' || autoUpgradedJiraSubProducts === undefined ? (
							<FormattedMessage
								id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-banner.banner-message-take-action"
								defaultMessage="You’re over the Free user limit, so we’ve temporarily moved you onto the Standard plan. Take action to avoid site deactivation. {takeAction}"
								description="Message in the auto-upgrade avoid deactivation banner, telling the user they are over the free user limit and need to take action to avoid deactivation of their product"
								values={{ takeAction: takeActionFormattedMessageValue }}
							/>
						) : (
							<FormattedMessage
								id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-banner.banner-message-take-action-product-specific"
								defaultMessage="{productsList} {numProducts, plural, one {is} other {are}} over the Free user limit, so we’ve temporarily moved you onto the Standard plan. Take action to avoid site deactivation. {takeAction}"
								description="Message in the auto-upgrade avoid deactivation banner, telling the user they are over the free user limit and need to take action to avoid deactivation of their product"
								values={{
									takeAction: takeActionFormattedMessageValue,
									productsList: formatList(
										autoUpgradedJiraSubProducts.map((product) =>
											getProductNameFromProductKey(product),
										),
										{ style: 'long', type: 'conjunction' },
									),
									numProducts: autoUpgradedJiraSubProducts.length,
								}}
							/>
						)}
					</Box>
					<ScreenEvent
						name={AVOID_DEACTIVATION_BANNER_SUBJECT_ID}
						attributes={{ baseProduct, autoUpgradedJiraSubProducts }}
					/>
				</Inline>
			</Flex>
		</AnalyticsContext>
	);
}
